/**
 * Shared grid rendering utilities for SquaresGrid, BoardExport, and PublicBoardViewPage.
 * These functions help render boards with different cell modes (standard, paired, quad).
 */

import type { CellMode, CellShape } from '../types';

/**
 * Get grid dimensions for merged cell rendering.
 * Returns { rows, cols, rowSpan, colSpan } for the visual grid.
 */
export function getMergedGridDimensions(
  cellMode: CellMode,
  cellShape: CellShape
): { rows: number; cols: number; rowSpan: number; colSpan: number } {
  if (cellMode === 'paired') {
    if (cellShape === '2x1') {
      // Vertical pairs: 5 visual rows x 10 cols, each cell spans 2 rows
      return { rows: 5, cols: 10, rowSpan: 2, colSpan: 1 };
    } else {
      // Horizontal pairs (1x2): 10 visual rows x 5 cols, each cell spans 2 cols
      return { rows: 10, cols: 5, rowSpan: 1, colSpan: 2 };
    }
  } else if (cellMode === 'quad') {
    // 2x2 quads: 5 visual rows x 5 cols, each cell spans 2 rows and 2 cols
    return { rows: 5, cols: 5, rowSpan: 2, colSpan: 2 };
  }
  // Standard mode: 10x10, no spanning
  return { rows: 10, cols: 10, rowSpan: 1, colSpan: 1 };
}

/**
 * Get the cell number for a visual grid position in merged mode.
 * Cell numbers are 1-indexed, left-to-right, top-to-bottom.
 */
export function getCellNumberForVisualPosition(
  visualRow: number,
  visualCol: number,
  cellMode: CellMode,
  cellShape: CellShape
): number {
  if (cellMode === 'paired') {
    if (cellShape === '2x1') {
      // Vertical pairs: 5 rows x 10 cols = 50 cells, numbered left-to-right, top-to-bottom
      return visualRow * 10 + visualCol + 1;
    } else {
      // Horizontal pairs: 10 rows x 5 cols = 50 cells
      return visualRow * 5 + visualCol + 1;
    }
  } else if (cellMode === 'quad') {
    // Quad: 5 rows x 5 cols = 25 cells
    return visualRow * 5 + visualCol + 1;
  }
  // Standard: 10 rows x 10 cols = 100 squares
  return visualRow * 10 + visualCol + 1;
}

/**
 * Get the first (top-left) square position for a given visual grid position.
 * Useful for getting the owner info from the grid.
 */
export function getFirstSquareForVisualPosition(
  visualRow: number,
  visualCol: number,
  cellMode: CellMode,
  cellShape: CellShape
): { row: number; col: number } {
  const { rowSpan, colSpan } = getMergedGridDimensions(cellMode, cellShape);
  return {
    row: visualRow * rowSpan,
    col: visualCol * colSpan,
  };
}

/**
 * Get all square positions that belong to a visual grid cell.
 */
export function getSquarePositionsForVisualCell(
  visualRow: number,
  visualCol: number,
  cellMode: CellMode,
  cellShape: CellShape
): { row: number; col: number }[] {
  const { rowSpan, colSpan } = getMergedGridDimensions(cellMode, cellShape);
  const baseRow = visualRow * rowSpan;
  const baseCol = visualCol * colSpan;

  const positions: { row: number; col: number }[] = [];
  for (let r = 0; r < rowSpan; r++) {
    for (let c = 0; c < colSpan; c++) {
      positions.push({ row: baseRow + r, col: baseCol + c });
    }
  }
  return positions;
}

/**
 * Calculate merged cell dimensions in pixels.
 */
export function getMergedCellSize(
  baseSize: number,
  gap: number,
  cellMode: CellMode,
  cellShape: CellShape
): { width: number; height: number } {
  const { rowSpan, colSpan } = getMergedGridDimensions(cellMode, cellShape);
  return {
    width: baseSize * colSpan + (colSpan - 1) * gap,
    height: baseSize * rowSpan + (rowSpan - 1) * gap,
  };
}

/**
 * Get the visual column index for a given actual column index.
 * Useful for header highlighting.
 */
export function getVisualColForActualCol(
  actualCol: number,
  cellMode: CellMode,
  cellShape: CellShape
): number {
  const { colSpan } = getMergedGridDimensions(cellMode, cellShape);
  return Math.floor(actualCol / colSpan);
}

/**
 * Get the visual row index for a given actual row index.
 * Useful for header highlighting.
 */
export function getVisualRowForActualRow(
  actualRow: number,
  cellMode: CellMode,
  cellShape: CellShape
): number {
  const { rowSpan } = getMergedGridDimensions(cellMode, cellShape);
  return Math.floor(actualRow / rowSpan);
}

/**
 * Get the numbers that should appear in a merged header cell.
 * Returns an array of numbers for the merged cell.
 */
export function getNumbersForMergedHeaderCell(
  numbers: number[],
  visualIndex: number,
  span: number
): number[] {
  const startIndex = visualIndex * span;
  return Array.from({ length: span }, (_, i) => numbers[startIndex + i]);
}

/**
 * Get the cell number (1-indexed) for a given underlying square position.
 * For standard mode, returns the square number (1-100).
 * For cell modes, returns the cell number (1-50 for paired, 1-25 for quad).
 */
export function getCellNumberForSquarePosition(
  row: number,
  col: number,
  cellMode: CellMode,
  cellShape: CellShape
): number {
  const { cols: visualCols, rowSpan, colSpan } = getMergedGridDimensions(cellMode, cellShape);
  const visualRow = Math.floor(row / rowSpan);
  const visualCol = Math.floor(col / colSpan);
  return visualRow * visualCols + visualCol + 1;
}
